import React, { useState } from 'react';
import { Container, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useGetAllEventTemplate } from '../services/react-query/event_templates';
import { Loading } from '../uikit';
import AppLink from '../components/common/AppLink/AppLink';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { UserConfigurableModule } from '../types/User';
import { archiveFilter } from '../components/common/filters/consts';
import { FilterState, IFilter } from '../components/common/filters/interfaces';
import { ROUTE_PATHS } from '../routes/Routes';
import { listPageLayoutStyles, listPageTableRowStyling } from '../styles/sxStyling';
import { useIsMobile } from '../hooks/useIsMobile';
import PaginationBar from '../components/PaginationBar';

export default function EventTemplatesList() {
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const { data, isLoading } = useGetAllEventTemplate({
    params: {
      page: currentPage,
      search: useSearchTerm(),
      filters: filterState,
    },
    enabled: true,
  });

  if (isLoading) return <Loading />;

  const eventTemplates = data?.data || [];
  const meta = data?.meta;
  const totalPages = Number(meta?.total_pages);

  const moduleFilters: IFilter[] = [archiveFilter];

  return (
    <Container maxWidth="xl">
      <ListHeader
        title="Event Templates"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={moduleFilters}
            module={UserConfigurableModule.EVENT_TEMPLATES}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={{ flex: 1 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Field Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventTemplates.map((template) => (
                  <TableRow key={template.uuid} sx={{ ...listPageTableRowStyling }}>
                    <TableCell>
                      <AppLink to={`/${ROUTE_PATHS.EVENT_TEMPLATES}/${template.uuid}`}>{template.name}</AppLink>
                    </TableCell>
                    <TableCell>{template.description}</TableCell>
                    <TableCell>{template.fields?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <PaginationBar page={currentPage} count={totalPages} onChange={(_e, v) => setCurrentPage(v)} />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
}
